// Where I was: the last server and, per server, the last channel I looked at, remembered on this device so the
// app reopens there (like Discord). A server I've never opened starts in #general. Storage can be missing or throw
// (private windows, blocked site data); then every visit just starts at the default.
const KEY = "bloom:last-visit";

interface LastVisit {
  server?: string;
  channels: Record<string, string>;
  /** Phones, Chats layout: the full-screen chat list was open when I left. */
  chatsOpen?: boolean;
}

function load(): LastVisit {
  try {
    const raw = JSON.parse(localStorage.getItem(KEY) ?? "{}") as Partial<LastVisit>;
    const channels = raw.channels && typeof raw.channels === "object" ? raw.channels : {};
    return {
      server: typeof raw.server === "string" ? raw.server : undefined,
      channels,
      chatsOpen: typeof raw.chatsOpen === "boolean" ? raw.chatsOpen : undefined,
    };
  } catch {
    return { channels: {} };
  }
}

/** The last server opened on this device. */
export const lastServer = () => load().server;

/** The last channel opened in this server on this device. */
export const lastChannelIn = (serverId: string): string | undefined => load().channels[serverId];

/** Phones, Chats layout: whether the chat list was open last time on this device. */
export const lastChatsOpen = (): boolean => !!load().chatsOpen;

export function rememberChatsOpen(open: boolean) {
  const v = load();
  if (!!v.chatsOpen === open) return;
  v.chatsOpen = open;
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    // fine: remembered for this visit only
  }
}

export function rememberVisit(serverId: string, channelId: string) {
  const v = load();
  if (v.server === serverId && v.channels[serverId] === channelId) return;
  v.server = serverId;
  v.channels[serverId] = channelId;
  try {
    localStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    // fine: remembered for this visit only
  }
}

/** Forget everything (signing out). */
export function forgetVisits() {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // nothing stored
  }
}
